var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import styled, { withTheme } from 'styled-components';
import { rem } from '../utils/Polished';
import SvgTick from '../Icon/SvgTick';
import SvgClose from '../Icon/SvgClose';
import SvgLoading from '../Icon/SvgLoading';
import Typography from '../Typography';
import { withForwardedRef } from '../utils/helpers/reactHelpers';
import uid from '../utils/uid';
const classList = {
    circle: '_ndsSwitch__circle',
    circleIcon: '_ndsSwitch__circle__icon',
    track: '_ndsSwitch__track',
    label: '_ndsSwitch__label',
    checked: '_ndsSwitch--checked',
    unchecked: '_ndsSwitch--unChecked',
    loading: '_ndsSwitch--loading',
    disabled: '_ndsSwitch--disabled',
    sizeSmall: '_ndsSwitch--small'
};
const SwitchSC = styled.div `
  display: inline-block;
  user-select: none;

  label {
    position: relative;
    height: ${rem('42px')};
    display: inline-flex;
    padding: 0;
    margin: 0 ${rem('5px')};
    z-index: ${(props) => props.theme.zIndex.default};
    box-sizing: border-box;
    flex-shrink: 0;
    vertical-align: middle;
    align-items: center;
    cursor: pointer;
    user-select: none;
    transition: box-shadow 0.3s linear;
    will-change: box-shadow;
  }

  &._ndsSwitch--small label {
    height: ${rem('30px')};
  }

  ._ndsSwitch__control {
    position: relative;
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
  }

  .${classList.circle} {
    flex: 0 0 auto;
    width: ${(props) => props.theme.SizeWidthSwitchCircleMedium};
    height: ${(props) => props.theme.SizeHeightSwitchCircleMedium};
    border-radius: 50%;
    border: solid
      ${(props) => props.theme.SizeBorderWidthSwitchCircleBase}
      ${(props) => props.theme.ColorBorderSwitchCircleBase};
    background-color: ${(props) => props.theme.ColorBackgroundSwitchCircleBase};
    box-sizing: inherit;
    position: absolute;
    top: -0.25rem;
    left: ${rem('0px')};
    display: flex;
    justify-content: center;
    align-items: center;
    transition: left 0.2s cubic-bezier(0.12, 0.08, 0.13, 0.13), background-color 0.3s,
      box-shadow 0.2s linear;
    will-change: left, background-color, box-shadow;

    &::before {
      position: absolute;
      content: '';
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      box-shadow: ${(props) => props.theme.SizeShadowSwitchCircleBase}
        ${(props) => props.theme.ColorShadowSwitchCircleBase};
      transition: box-shadow 0.2s linear;
      border-radius: 100%;
      will-change: box-shadow;
    }
  }

  &._ndsSwitch--small .${classList.circle} {
    width: ${(props) => props.theme.SizeWidthSwitchCircleSmall};
    height: ${(props) => props.theme.SizeHeightSwitchCircleSmall};
  }

  .${classList.circleIcon} {
    flex-grow: 1;
    fill: ${(props) => props.theme.ColorIconSwitchBase};
  }

  .${classList.track} {
    flex: 0 0 auto;
    width: ${(props) => props.theme.SizeWidthSwitchTrackMedium};
    height: ${(props) => props.theme.SizeHeightSwitchTrackMedium};
    border-radius: ${(props) => props.theme.SizeBorderRadiusSwitchTrackBase};
    background-color: ${(props) => props.theme.ColorBackgroundSwitchTrackBase};
    transition: background-color 0.3s;
    will-change: background-color;
    box-sizing: border-box;
  }

  &._ndsSwitch--small .${classList.track} {
    width: ${(props) => props.theme.SizeWidthSwitchTrackSmall};
    height: ${(props) => props.theme.SizeHeightSwitchTrackSmall};
  }

  input {
    position: absolute;
    left: -100%;
    opacity: 0;
    z-index: ${(props) => props.theme.zIndex.default - 1};
    height: 0;
    width: 0;
  }

  .${classList.label} {
    white-space: nowrap;
    margin: 0 ${rem('24px')} 0 0;
  }

  &._ndsSwitch--small .${classList.label} {
    margin: 0 ${rem('32px')} 0 0;
  }

  /* focus */
  label:focus {
    outline: 0;
  }

  /* hover */
  label:focus,
  &.${classList.checked}:hover,:hover {
    .${classList.circle} {
      box-shadow: ${(props) => props.theme.SizeShadowSwitchCircleHover}
        ${(props) => props.theme.ColorShadowSwitchCircleHover};
    }
  }

  /* loading */
  &.${classList.loading} {
    label {
      cursor: wait;
    }
    &:active {
      .${classList.circle} {
        box-shadow: none;
      }
    }
    .${classList.circleIcon} {
      fill: ${(props) => props.theme.ColorIconSwitchLoading};
    }
    .${classList.track} {
      background-color: ${(props) => props.theme.ColorBackgroundSwitchTrackLoading};
    }
  }

  /* checked */
  &.${classList.checked} {
    .${classList.circle} {
      left: ${rem('16px')};
    }
    .${classList.circleIcon} {
      fill: ${(props) => props.theme.ColorIconSwitchChecked};
    }
    .${classList.track} {
      background-color: ${(props) => props.theme.ColorBackgroundSwitchTrackChecked};
    }
  }

  &.${classList.checked}._ndsSwitch--small {
    .${classList.circle} {
      left: ${rem('17px')};
    }
  }

  /* disabled */
  &.${classList.disabled}, &.${classList.disabled}:hover, &.${classList.disabled}:active {
    label {
      cursor: not-allowed;
    }

    .${classList.track}, .${classList.circle} {
      background-color: ${(props) => props.theme.ColorBackgroundSwitchCircleDisabled};
      box-shadow: none;
    }
    .${classList.circleIcon} {
      fill: ${(props) => props.theme.ColorIconSwitchDisabled};
    }
  }
`;
const SwitchComp = (props) => {
    const { checked: checkedProp, disabled, id, name, inputRef: inputRefProp, forwardedRef, onChange, required, size, value, loading, className: classNameProp, theme, label } = props, other = __rest(props, ["checked", "disabled", "id", "name", "inputRef", "forwardedRef", "onChange", "required", "size", "value", "loading", "className", "theme", "label"]);
    const { current: isControlled } = React.useRef(checkedProp != null);
    const [checkedState, setCheckedState] = React.useState(Boolean(checkedProp));
    const inputId = React.useMemo(() => id || uid('_ndsSwitch_'), [id]);
    const labelId = `label-${inputId}`;
    const { current: inputRef } = React.useRef(inputRefProp || React.createRef());
    const handleInputChange = React.useCallback((event) => {
        if (!isControlled) {
            setCheckedState(event.target.checked);
        }
        if (onChange) {
            onChange(event);
        }
    }, [onChange, isControlled]);
    const toggleCheckBox = React.useCallback((event) => {
        event.preventDefault();
        inputRef.current.click();
    }, [inputRef]);
    const checked = isControlled ? checkedProp : checkedState;
    const isLoading = !disabled && loading;
    const isDisabled = disabled || loading;
    const IconComp = checked ? SvgTick : SvgClose;
    const { ColorFontPrimary } = theme;
    const iconSize = size === 's' ? 's' : 'm';
    const tabIndex = 0;
    return (React.createElement(SwitchSC, Object.assign({ className: clsx({
            [classList.checked]: checked,
            [classList.loading]: loading,
            [classList.disabled]: disabled,
            [classList.sizeSmall]: size === 's'
        }, classNameProp), size: size, ref: forwardedRef }, other),
        React.createElement("label", { id: labelId, tabIndex: tabIndex, htmlFor: inputId, onKeyPress: toggleCheckBox },
            label && (React.createElement(Typography, { variant: "body2", className: classList.label }, label)),
            React.createElement("div", { className: "_ndsSwitch__control" },
                React.createElement("div", { className: classList.circle }, isLoading ? (React.createElement(SvgLoading, { size: iconSize, color: ColorFontPrimary, className: classList.circleIcon })) : (React.createElement(IconComp, { className: classList.circleIcon, size: iconSize }))),
                React.createElement("div", { className: classList.track }),
                React.createElement("input", { type: "checkbox", checked: checked, disabled: isDisabled, id: inputId, name: name, ref: inputRef, onChange: handleInputChange, required: required, value: value, "aria-labelledby": labelId })))));
};
SwitchComp.propTypes = {
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    id: PropTypes.string,
    name: PropTypes.string,
    inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    forwardedRef: PropTypes.any,
    onChange: PropTypes.func,
    required: PropTypes.bool,
    size: PropTypes.oneOf(['s', 'm']),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    loading: PropTypes.bool,
    className: PropTypes.string,
    theme: PropTypes.oneOfType([PropTypes.any]),
    label: PropTypes.string
};
SwitchComp.defaultProps = {
    label: undefined,
    checked: undefined,
    disabled: false,
    loading: false,
    required: false,
    onChange: null,
    size: 'm',
    id: undefined,
    name: undefined,
    value: undefined,
    className: undefined,
    inputRef: null,
    forwardedRef: null,
    theme: {}
};
SwitchComp.displayName = 'Switch';
const Switch = withTheme(SwitchComp);
export { SwitchComp as SwitchRootComp };
export default withForwardedRef(Switch);
//# sourceMappingURL=Switch.js.map